import React from 'react';
import { Button } from './ui/button';
import { FileDown, FileSpreadsheet } from 'lucide-react';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

function getToday() {
  return new Date().toISOString().split('T')[0];
}

const REPORT_NAMES = {
  overview:          'Verksamhetsöversikt',
  'attendance-trend': 'Närvaroutveckling',
  'goal-summary':    'Måluppfyllelse',
  deviations:        'Avvikelserapport',
  'period-summary':  'Periodöversikt',
};

const ReportExportButtons = ({ data, reportType, period, role }) => {
  if (!data) return null;

  const isParticipant = role === 'read_only_participant';
  const reportName = REPORT_NAMES[reportType] || reportType;
  const periodStr = period ? `${period.from} — ${period.to}` : getToday();

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('AIUS Control', pageW / 2, y, { align: 'center' });
    y += 8;

    doc.setFontSize(12);
    doc.text(reportName, pageW / 2, y, { align: 'center' });
    y += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${periodStr}`, pageW / 2, y, { align: 'center' });
    y += 5;
    doc.text(`Genererad: ${getToday()}`, pageW / 2, y, { align: 'center' });
    y += 14;

    doc.setLineWidth(0.3);
    doc.line(14, y, pageW - 14, y);
    y += 8;

    const primitiveEntries = Object.entries(data).filter(
      ([, v]) => typeof v !== 'object' || v === null
    );
    doc.setFontSize(9);
    for (const [k, v] of primitiveEntries) {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.text(`${k}:`, 14, y);
      doc.setFont('helvetica', 'bold');
      doc.text(String(v ?? '—'), 70, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
    }

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('AIUS Control — aiuscore.se', pageW / 2, pageH - 10, { align: 'center' });

    doc.save(`${reportType}_${getToday()}.pdf`);
    toast.success('PDF exporterad');
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    const kpiRows = Object.entries(data)
      .filter(([, v]) => typeof v !== 'object' || v === null)
      .map(([k, v]) => ({ KPI: k, Värde: v ?? '' }));

    if (kpiRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(kpiRows), 'Sammanfattning');
    }

    const detailKey = Object.keys(data).find(
      k => Array.isArray(data[k]) && data[k].length > 0
    );
    if (detailKey && Array.isArray(data[detailKey]) && data[detailKey].length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[detailKey]), 'Detaljer');
    }

    XLSX.writeFile(wb, `${reportType}_${getToday()}.xlsx`);
    toast.success('Excel exporterad');
  };

  return (
    <div className="flex gap-2" data-testid="export-buttons">
      <Button
        variant="outline"
        size="sm"
        onClick={exportPDF}
        className="rounded-full text-xs"
        data-testid="export-pdf-btn"
      >
        <FileDown size={14} className="mr-1.5" />
        Exportera PDF
      </Button>
      {!isParticipant && (
        <Button
          variant="outline"
          size="sm"
          onClick={exportExcel}
          className="rounded-full text-xs"
          data-testid="export-excel-btn"
        >
          <FileSpreadsheet size={14} className="mr-1.5" />
          Exportera Excel
        </Button>
      )}
    </div>
  );
};

export default ReportExportButtons;
